import { friendlyApiMessage } from "@atlas/shared";
import type { RouteAnalysis, RouteAnalyserAllowance, RouteShare, RouteShareCreated } from "@atlas/shared";
import { API_BASE_URL, apiDelete, apiGet } from "../lib/api-client";

export type { RouteAnalysis, RouteAnalyserAllowance, RouteShare, RouteShareCreated } from "@atlas/shared";

// Route analyser (spec 27). POST /route-analyser/analyse is served by its own
// Lambda with reserved concurrency 3. When it's full, Lambda answers 429
// before Hono runs, with no CORS headers, so the browser usually reports a
// failed fetch rather than a readable status (a CloudFront 504 looks the
// same). Treat every unreadable failure as "busy".

export const ROUTE_ANALYSER_COPY = {
  busy: "Busy right now, try again in a minute.",
  timeout: "That route took too long to check. Try again, or split a multi-day route into days.",
  unavailable: "Route checks are switched off for signed-out visitors at the moment. Sign in to check a route.",
} as const;

// Spec 27 V9: a touched section left under 40% after the walk is a path the
// route only crosses, not a near miss. Listed collapsed under its own heading,
// and kept out of the summary line, the delta chips and the map's gap overlay.
export const CROSSED_SECTION_FRACTION = 0.4;

export function isCrossedSection(s: { fractionAfter: number; completes?: boolean }): boolean {
  return s.completes !== true && s.fractionAfter < CROSSED_SECTION_FRACTION;
}

export type RouteAnalyserErrorKind = "busy" | "timeout" | "unavailable" | "invalid" | "other";

export class RouteAnalyserError extends Error {
  constructor(
    public readonly kind: RouteAnalyserErrorKind,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "RouteAnalyserError";
  }
}

export interface AnalyseRouteOptions {
  /** Ask for the personal diff (Pro, or spends a free account's monthly check). */
  personal?: boolean;
  /** Cloudflare Turnstile token, signed-out requests only. Single-use. */
  turnstileToken?: string | null;
}

/**
 * Multipart upload, cookie session. Deliberately a raw fetch rather than the
 * shared client: running out of the free allowance is a 200 here, and the
 * analyser never 402s, so nothing on this path should reach the global
 * upsell handling. No custom headers either, so the request stays a CORS
 * "simple" request (multipart/form-data, no preflight).
 */
export async function analyseRoute(file: File, opts: AnalyseRouteOptions = {}): Promise<RouteAnalysis> {
  const form = new FormData();
  form.append("file", file);
  if (opts.personal) form.append("personal", "1");
  if (opts.turnstileToken) form.append("turnstileToken", opts.turnstileToken);
  return postToAnalyser<RouteAnalysis>("/route-analyser/analyse", form);
}

/**
 * Makes a share link (signed in). Sends the same GPX again: the server takes
 * its own generic snapshot rather than trusting a result from the page.
 */
export function createRouteShare(file: File): Promise<RouteShareCreated> {
  const form = new FormData();
  form.append("file", file);
  return postToAnalyser<RouteShareCreated>("/route-analyser/shares", form);
}

/** A viewer's own check of a shared route (signed in; spends a free account's monthly check). */
export function analyseRouteShare(id: string): Promise<RouteAnalysis> {
  return postToAnalyser<RouteAnalysis>(`/route-analyser/shares/${encodeURIComponent(id)}/analyse`);
}

/** Public. Served by the main API, not the capped analyser function. */
export function fetchRouteShare(id: string): Promise<RouteShare> {
  return apiGet(`/route-shares/${encodeURIComponent(id)}`);
}

export function deleteRouteShare(id: string): Promise<{ ok: true }> {
  return apiDelete(`/route-shares/${encodeURIComponent(id)}`);
}

async function postToAnalyser<T>(path: string, form?: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
  } catch {
    throw new RouteAnalyserError("busy", ROUTE_ANALYSER_COPY.busy);
  }

  if (res.ok) {
    try {
      return (await res.json()) as T;
    } catch {
      throw new RouteAnalyserError("busy", ROUTE_ANALYSER_COPY.busy, res.status);
    }
  }

  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  const code = body && typeof body.error === "string" ? body.error : null;

  if (code === "analysis_timeout") {
    throw new RouteAnalyserError("timeout", ROUTE_ANALYSER_COPY.timeout, res.status);
  }
  if (code === "route_analyser_unavailable") {
    throw new RouteAnalyserError("unavailable", ROUTE_ANALYSER_COPY.unavailable, res.status);
  }
  if (!code && [429, 502, 503, 504].includes(res.status)) {
    throw new RouteAnalyserError("busy", ROUTE_ANALYSER_COPY.busy, res.status);
  }
  if (res.status === 429) {
    // A readable 429 is the app's own hourly limiter, not the concurrency
    // cap, so "a minute" would be wrong: use the shared Retry-After copy.
    throw new RouteAnalyserError(
      "other",
      friendlyApiMessage(429, code ?? undefined, retryAfterSeconds(res)),
      429,
    );
  }
  if (res.status === 400 && code) {
    throw new RouteAnalyserError("invalid", code, 400);
  }
  if (res.status >= 500) {
    throw new RouteAnalyserError("busy", ROUTE_ANALYSER_COPY.busy, res.status);
  }
  throw new RouteAnalyserError("other", friendlyApiMessage(res.status, code ?? undefined), res.status);
}

function retryAfterSeconds(res: Response): number | undefined {
  const value = Number(res.headers.get("Retry-After"));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/** Signed-in only: labels the personal button before an upload. */
export function fetchRouteAnalyserAllowance(): Promise<RouteAnalyserAllowance> {
  return apiGet("/route-analyser/allowance");
}
